import numbro from 'numbro';
import React, { useMemo, useState } from 'react';
import { LinkButton } from '../../../CommonComponents/Button';
import Chart from '../../../CommonComponents/Chart';
import { formatNumberLegacy } from '../../../CommonUtils/formatNumberLegacy';
import { Panel, TaxBrackets } from './RothConversionStyles';
import { RothConversionProps } from './RothConversionTypes';
import { TaxBracketOption, TaxBracketPopover } from './TaxBracketPopover';
import { formatPercentage } from '../../../CommonUtils/formatNumber';

const RothConversionChart: React.FC<RothConversionProps> = ({
  brackets: rawBrackets = [],
  maxTaxBracket,
  conversionAmount,
  yearlyTaxBrackets,
  series,
}) => {
  const [brackets, setBrackets] = useState<TaxBracketOption[]>(
    rawBrackets
      .sort((a, b) => a.value - b.value)
      .map(bracket => ({
        ...bracket,
        selected: maxTaxBracket?.value === bracket.value,
        value: bracket.value / 100,
      })),
  );

  // const linesBefore = bracketsBefore2026.map(({ selected, value, data, color }) => ({
  //   type: 'line',
  //   name: `Tax Bracket (${formatPercentage(value)})`,
  //   color,
  //   data,
  //   visible: selected,
  // }));

  const linesStarting = brackets.map(({ selected, value, data, color }) => ({
    type: 'line',
    name: `Tax Bracket (${formatPercentage(value)})`,
    color,
    data,
    visible: selected,
  }));

  const chartConfig = useMemo(
    () => ({
      chart: {
        style: {
          fontFamily: 'Open Sans, sans-serif',
        },
      },
      credits: {
        enabled: false,
      },
      title: {
        align: 'left',
        text: 'Roth Conversion',
        style: {
          fontWeight: '600',
          fontSize: '18px',
          color: '#444',
        },
      },
      tooltip: {
        shared: true,
        backgroundColor: '#fff',
        borderRadius: 5,
        borderWidth: 1,
        shadow: false,
        padding: 8,
        borderColor: '#ddd',
        useHTML: true,
        formatter() {
          return `<div style="font-size: 10px; line-height: 1.7em;">
            <div style="font-size: 12px; color: #767676;">Conversion Details</div>
            <div>
              Tax Bracket &nbsp;
              <span style="font-weight: 600;">
                ${
                  yearlyTaxBrackets[(this as any).x]
                    ? `<span style="color: ${
                        yearlyTaxBrackets[(this as any).x].color
                      };">&#9679;</span> ${formatPercentage(
                        yearlyTaxBrackets[(this as any).x].value / 100,
                      )}`
                    : ''
                }
              </span>
            </div>
            
            <div>Amount &nbsp; <span style="font-weight: 600;">${formatNumberLegacy(
              conversionAmount[(this as any).x],
              {
                type: 'currency',
              },
            )}</span></div>
          </div>`;
        },
      },
      legend: {
        align: 'right',
        verticalAlign: 'top',
        symbolWidth: 8,
        symbolHeight: 8,
        itemStyle: {
          fontSize: '16px',
          fontWeight: 'normal',
        },
      },
      yAxis: {
        title: { enabled: false },
        labels: {
          formatter() {
            return numbro((this as any).value).formatCurrency({ average: true });
          },
        },
      },
      xAxis: {
        tickWidth: 0,
      },
      plotOptions: {
        line: {
          showInLegend: false,
          marker: {
            enabled: false,
          },
        },
        column: {
          stacking: 'normal',
        },
      },
      series: [
        {
          type: 'column',
          name: 'Current',
          data: series.current,
          color: '#BADFEF',
          stack: 'current',
          legendIndex: 0,
        },
        {
          type: 'column',
          name: 'Conversion Amount',
          data: series.recommended.map(([year]) => [year, conversionAmount[year]]),
          color: '#3e4e88',
          stack: 'proposed',
          legendIndex: 2,
        },
        {
          type: 'column',
          name: 'Proposed',
          data: series.recommended.map(([year, value]) => [
            year,
            Math.max(0, value - conversionAmount[year]),
          ]),
          color: '#5090DE',
          stack: 'proposed',
          legendIndex: 1,
        },
        // ...linesBefore,
        ...linesStarting,
      ],
    }),
    [conversionAmount, linesStarting, series, yearlyTaxBrackets],
  );

  return (
    <Panel>
      <Chart options={chartConfig} />

      <TaxBrackets>
        <TaxBracketPopover values={brackets} onChange={setBrackets}>
          <LinkButton appendIcon="fas fa-caret-down">Tax Bracket</LinkButton>
        </TaxBracketPopover>

        {/*
          <TaxBracketPopover values={bracketsBefore2026} onChange={setBracketsBefore2026}>
            <LinkButton appendIcon="fas fa-caret-down">
              {taxProvisionsExpire ? 'Bracket before 2026' : 'Tax Bracket'}
            </LinkButton>
          </TaxBracketPopover>

          {taxProvisionsExpire && (
            <TaxBracketPopover values={bracketsStarting2026} onChange={setBracketsStarting2026}>
              <LinkButton appendIcon="fas fa-caret-down">Bracket starting 2026</LinkButton>
            </TaxBracketPopover>
          )}
        */}
      </TaxBrackets>
    </Panel>
  );
};

export default RothConversionChart;
